import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { IconList } from '@tabler/icons-react';
import { useShellProfile } from '../../state/useShellProfile';
import { useListPortalCalendarEventsQuery } from '../../state/domainRouterApi';
import { SupportErrorMessage } from '../../components/overlays/EngineeringSupportBanner';

const Empty = ({ children }: { children: ReactNode }) => (
  <p className="text-sm text-irongrey/50 font-medium">{children}</p>
);

const ErrorState = ({ children }: { children: ReactNode }) => (
  <SupportErrorMessage>{children}</SupportErrorMessage>
);

const LoadingRows = () => (
  <ul className="divide-y divide-gray-100" aria-hidden>
    {Array.from({ length: 7 }).map((_, idx) => (
      <li key={idx} className="px-6 py-4">
        <div className="animate-pulse">
          <div className="h-4 w-40 rounded bg-gray-100 mb-2" />
          <div className="h-3 w-64 rounded bg-gray-100" />
        </div>
      </li>
    ))}
  </ul>
);

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const readText = (value: unknown): string => String(value ?? '').trim();

const toTitleCase = (value: string): string => value
  .replace(/[-_]+/g, ' ')
  .split(' ')
  .filter(Boolean)
  .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1).toLowerCase()}`)
  .join(' ');

const readEventType = (eventRecord: Record<string, unknown>): string =>
  readText(eventRecord.eventType ?? eventRecord.type).toLowerCase();

const readEventId = (eventRecord: Record<string, unknown>, index = 0): string =>
  readText(eventRecord.id) || `${index}`;

const readEventApplicationId = (eventRecord: Record<string, unknown>): string =>
  readText(eventRecord.applicationId);

const readEventTimestampMs = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1_000_000_000_000 ? value : value * 1000;
  }
  const text = readText(value);
  if (!text) return null;
  const numeric = Number(text);
  if (Number.isFinite(numeric)) {
    return numeric > 1_000_000_000_000 ? numeric : numeric * 1000;
  }
  const parsed = Date.parse(text);
  return Number.isFinite(parsed) ? parsed : null;
};

const readEventStartMs = (eventRecord: Record<string, unknown>): number | null =>
  readEventTimestampMs(eventRecord.startDateTime ?? eventRecord.startDate ?? eventRecord.date ?? eventRecord.occurredAt);

const readEventEndMs = (eventRecord: Record<string, unknown>): number | null =>
  readEventTimestampMs(eventRecord.endDateTime ?? eventRecord.endDate);

const formatEventDate = (timestampMs: number | null): string => {
  if (timestampMs == null) return 'Date pending';
  return new Date(timestampMs).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatEventTime = (timestampMs: number | null): string => {
  if (timestampMs == null) return 'Time pending';
  return new Date(timestampMs).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatEventTimeRange = (eventRecord: Record<string, unknown>): string => {
  const startMs = readEventStartMs(eventRecord);
  const endMs = readEventEndMs(eventRecord);
  if (startMs == null) return 'Time pending';
  if (endMs == null) return formatEventTime(startMs);
  return `${formatEventTime(startMs)} - ${formatEventTime(endMs)}`;
};

const getEventTitle = (eventRecord: Record<string, unknown>): string => {
  const explicitTitle = readText(eventRecord.title ?? eventRecord.summary);
  if (explicitTitle) return explicitTitle;

  const eventType = readEventType(eventRecord);
  if (eventType === 'interview') return 'Admissions Interview';
  if (eventType === 'visit') return 'Admissions Visit';
  return toTitleCase(eventType || 'Event');
};

const withQuery = (pathname: string, query: Record<string, string>): string => {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    const normalized = readText(value);
    if (!normalized) return;
    search.set(key, normalized);
  });
  const encoded = search.toString();
  return encoded ? `${pathname}?${encoded}` : pathname;
};

const getEventTarget = (eventRecord: Record<string, unknown>, index: number): string => withQuery(
  '/parent/portal/calendar',
  {
    eventId: readEventId(eventRecord, index),
    applicationId: readEventApplicationId(eventRecord),
    eventType: readEventType(eventRecord),
  },
);

type EventRowProps = {
  eventRecord: Record<string, unknown>;
  index: number;
  selected?: boolean;
};

const EventRow = ({ eventRecord, index, selected = false }: EventRowProps) => {
  const title = getEventTitle(eventRecord);
  const when = formatEventDate(readEventStartMs(eventRecord));
  const time = formatEventTimeRange(eventRecord);
  const location = readText(eventRecord.location);
  const description = readText(eventRecord.description);
  const referenceCode = readText(eventRecord.referenceCode);
  const target = getEventTarget(eventRecord, index);

  return (
    <li className="px-2 py-2">
      <Link
        to={target}
        className={`block rounded-xl px-4 py-3 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${selected ? 'bg-primary/5 ring-2 ring-primary/20' : 'hover:bg-gray-50'}`}
        data-testid={selected ? 'parent-portal-calendar-selected-event-row' : 'parent-portal-calendar-event-row'}
      >
        <article className="flex items-start justify-between gap-4">
          <header className="min-w-0">
            <h2 className="text-sm font-bold text-irongrey truncate">{title}</h2>
            {description ? <p className="text-xs text-irongrey/60 font-medium mt-1">{description}</p> : null}
            {referenceCode ? (
              <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
                Application {referenceCode}
              </p>
            ) : null}
          </header>
          <p className="text-right">
            <time className="block text-xs font-bold text-irongrey/40 uppercase tracking-widest">
              {when}
            </time>
            <span className="block text-xs text-irongrey/60 font-medium mt-1">{time}</span>
            {location ? <span className="block text-xs text-irongrey/60 font-medium mt-1">{location}</span> : null}
          </p>
        </article>
      </Link>
    </li>
  );
};

const EventDetailCard = ({ eventRecord }: { eventRecord: Record<string, unknown> }) => {
  const title = getEventTitle(eventRecord);
  const date = formatEventDate(readEventStartMs(eventRecord));
  const time = formatEventTimeRange(eventRecord);
  const location = readText(eventRecord.location) || 'School office will confirm the location.';
  const referenceCode = readText(eventRecord.referenceCode);
  const applicationId = readEventApplicationId(eventRecord);
  const childApplicationId = readText(eventRecord.childApplicationId);
  const familyName = readText(eventRecord.familyName);

  return (
    <section
      className="rounded-3xl border border-primary/20 bg-primary/5 p-5 shadow-sm"
      data-testid="parent-portal-calendar-selected-event"
      aria-label="Selected interview details"
    >
      <p className="text-[10px] font-bold uppercase tracking-widest text-primary">Notification destination</p>
      <h2 className="mt-1 text-2xl font-bold text-irongrey">{title}</h2>
      <dl className="mt-4 grid gap-3 text-sm md:grid-cols-2">
        <div>
          <dt className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">Date</dt>
          <dd className="mt-1 font-semibold text-irongrey">{date}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">Time</dt>
          <dd className="mt-1 font-semibold text-irongrey">{time}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">Application</dt>
          <dd className="mt-1 font-semibold text-irongrey">{referenceCode || applicationId || 'Application pending'}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">Child</dt>
          <dd className="mt-1 font-semibold text-irongrey">{childApplicationId || 'Child details pending'}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">Family</dt>
          <dd className="mt-1 font-semibold text-irongrey">{familyName || 'Family pending'}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">Location</dt>
          <dd className="mt-1 font-semibold text-irongrey">{location}</dd>
        </div>
      </dl>
      <p className="mt-4 text-xs font-semibold text-irongrey/60">
        Please arrive a few minutes early and contact the admissions office if this interview time needs to change.
      </p>
    </section>
  );
};

const isSupportedCalendarEventType = (value: string): value is 'interview' | 'visit' =>
  value === 'interview' || value === 'visit';

export function PortalCalendarPage() {
  const { profile } = useShellProfile();
  const [searchParams] = useSearchParams();
  const actorId = profile?.userSub ?? null;
  const targetEventId = readText(searchParams.get('eventId'));
  const targetApplicationId = readText(searchParams.get('applicationId'));
  const targetEventType = readText(searchParams.get('eventType')).toLowerCase();
  const targetNotificationId = readText(searchParams.get('notificationId'));
  const eventTypes = isSupportedCalendarEventType(targetEventType) ? [targetEventType] : undefined;
  const calendarArgs = useMemo(() => (
    targetApplicationId || eventTypes
      ? {
          applicationId: targetApplicationId || undefined,
          eventTypes,
        }
      : undefined
  ), [eventTypes, targetApplicationId]);

  const calendarQuery = useListPortalCalendarEventsQuery(
    calendarArgs,
    { skip: !actorId },
  );

  const eventRecords = useMemo(
    () => (calendarQuery.data ?? []).map((event) => asRecord(event)),
    [calendarQuery.data],
  );
  const selectedEvent = useMemo(() => {
    if (!targetEventId && !targetApplicationId && !targetEventType) return null;
    return eventRecords.find((eventRecord, index) => {
      const matchesEventId = targetEventId
        ? readEventId(eventRecord, index) === targetEventId
        : true;
      const matchesApplication = targetApplicationId
        ? readEventApplicationId(eventRecord) === targetApplicationId
        : true;
      const matchesType = targetEventType && isSupportedCalendarEventType(targetEventType)
        ? readEventType(eventRecord) === targetEventType
        : true;
      return matchesEventId && matchesApplication && matchesType;
    }) ?? null;
  }, [eventRecords, targetApplicationId, targetEventId, targetEventType]);
  const hasTargetedNotification = Boolean(targetNotificationId || targetEventId || targetApplicationId || targetEventType);

  return (
    <main
      data-testid="parent-portal-calendar"
      data-loaded={actorId && !calendarQuery.isLoading ? "true" : "false"}
      data-extracted-from="owned:ystnj/parent-portal/calendar"
      className="w-full space-y-6"
    >
      <header className="space-y-2">
        <section className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center" aria-hidden>
            <IconList size={20} className="text-irongrey/40" />
          </span>
          <h2 className="text-4xl font-bold text-irongrey tracking-tight">Portal</h2>
          <p className="sr-only">
            Family calendar timeline for conferences, billing milestones, admissions deadlines, and school-wide events.
          </p>
        </section>
      </header>

      {!actorId ? <ErrorState>Unable to resolve your account identity.</ErrorState> : null}
      {calendarQuery.error ? <ErrorState>Unable to load calendar right now.</ErrorState> : null}

      {hasTargetedNotification && calendarQuery.isLoading ? (
        <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
          <Empty>Loading interview details...</Empty>
        </section>
      ) : null}
      {hasTargetedNotification && !calendarQuery.isLoading && !calendarQuery.error && selectedEvent ? (
        <EventDetailCard eventRecord={selectedEvent} />
      ) : null}
      {hasTargetedNotification && !calendarQuery.isLoading && !calendarQuery.error && !selectedEvent ? (
        <section className="rounded-3xl border border-amber-200 bg-amber-50 p-5" data-testid="parent-portal-calendar-selected-event-missing">
          <p className="text-sm font-bold text-amber-900">Interview details are not available yet.</p>
          <p className="mt-1 text-sm font-semibold text-amber-800/80">
            The admissions office may still be finalizing this event. Please check the calendar again shortly.
          </p>
        </section>
      ) : null}

      <section className="bg-white border border-gray-100 rounded-3xl shadow-card overflow-hidden" aria-label="Calendar events">
        {calendarQuery.isLoading ? <LoadingRows /> : null}
        {!calendarQuery.isLoading && !calendarQuery.error && eventRecords.length === 0 ? (
          <section className="px-6 py-6">
            <Empty>No events scheduled.</Empty>
          </section>
        ) : null}
        <ul className="divide-y divide-gray-100">
          {eventRecords.map((eventRecord, idx) => (
            <EventRow
              key={`${readEventId(eventRecord, idx)}-${idx}`}
              eventRecord={eventRecord}
              index={idx}
              selected={selectedEvent === eventRecord}
            />
          ))}
        </ul>
      </section>
    </main>
  );
}
